import { useState, useCallback, useMemo } from "react";
import cloudApi from "../../../services/cloudApi";
import { copyToClipboard, getExportStats, generateFilename } from "../utils/exportUtils";

// Maximum rows that can be copied to clipboard (memory/performance constraint)
const CLIPBOARD_ROW_LIMIT = 1000;

// Display-only columns that should never be exported (checked against both id and type)
const NON_EXPORTABLE_COLUMNS = new Set(["image_previews", "image_group_toggle"]);

/**
 * Filter out columns that are display-only and shouldn't be exported
 * @param {Array} columns - Array of column definitions
 * @param {boolean} hideImages - Whether to exclude image columns from export
 * @param {boolean} hideDebugColumns - Whether to exclude debug columns (raw JSON-LD etc.) from export
 * @returns {Array} - Filtered array of exportable columns
 */
const getExportableColumns = (columns, hideImages = false, hideDebugColumns = true) => {
  if (!columns) return [];

  return columns.filter((col) => {
    // Filter out display-only columns
    if (NON_EXPORTABLE_COLUMNS.has(col.id) || NON_EXPORTABLE_COLUMNS.has(col.type)) {
      return false;
    }
    // Filter out image columns when hideImages is true
    if (hideImages && col.type === "image") {
      return false;
    }
    // Filter out debug columns (raw JSON-LD, *__json, etc.) unless the user revealed them —
    // exports mirror the table, which hides debug columns by default.
    if (hideDebugColumns && col?.debug) {
      return false;
    }
    return true;
  });
};

/**
 * Custom hook for managing cloud export functionality
 * Exports are handled by the backend API for CSV/Excel/JSON
 * Clipboard copy fetches all rows from API (up to 1000 rows limit)
 *
 * @param {Array} filteredData - Filtered data to export (current page/view)
 * @param {Array} totalData - Total data for stats
 * @param {Array} displayColumns - Column definitions
 * @param {Object} tableHeader - Table metadata with ID
 * @param {Function} onExportSuccess - Callback when export is successful
 * @param {number} totalRowCount - Total number of rows in the table (for clipboard limit check)
 * @param {boolean} hideImages - Whether to exclude image columns from export
 * @param {boolean} hideDebugColumns - Whether to exclude debug columns from export (default true)
 * @returns {Object} Export functions and state
 */
export const useCloudExport = (filteredData, totalData, displayColumns, tableHeader, onExportSuccess, totalRowCount = 0, hideImages = false, hideDebugColumns = true) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null); // Track which format is being exported
  const [exportMessage, setExportMessage] = useState(null);

  // Filter out display-only columns for export (image columns when hideImages, debug columns
  // unless the user revealed them).
  const exportableColumns = useMemo(() => {
    return getExportableColumns(displayColumns, hideImages, hideDebugColumns);
  }, [displayColumns, hideImages, hideDebugColumns]);

  // Get column IDs for API export
  const exportColumnIds = useMemo(() => {
    return exportableColumns.map((col) => col.id);
  }, [exportableColumns]);

  // Calculate export statistics
  const exportStats = useMemo(() => {
    return getExportStats(filteredData, totalData, exportableColumns);
  }, [filteredData, totalData, exportableColumns]);

  // Check if clipboard copy should be disabled (over row limit)
  const clipboardDisabled = useMemo(() => {
    return totalRowCount > CLIPBOARD_ROW_LIMIT;
  }, [totalRowCount]);

  const clipboardDisabledReason = useMemo(() => {
    if (!clipboardDisabled) return null;
    return `Tables over ${CLIPBOARD_ROW_LIMIT.toLocaleString()} rows cannot be copied to clipboard. Use CSV export instead.`;
  }, [clipboardDisabled]);

  // Generate filename for client-side exports
  const filename = useMemo(() => {
    return generateFilename(tableHeader, exportStats);
  }, [tableHeader, exportStats]);

  // Clear export message after timeout
  const clearMessage = useCallback(() => {
    setTimeout(() => setExportMessage(null), 4000);
  }, []);

  // Export as CSV via API
  const handleExportCSV = useCallback(async () => {
    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return;
    }

    setIsExporting(true);
    setExportingFormat('csv');
    setExportMessage({ type: "loading", text: "Preparing CSV export..." });

    try {
      const result = await cloudApi.downloadExport(
        tableHeader.id,
        'csv',
        exportColumnIds,
        filename.replace(/\.(xlsx|json)$/, '.csv')
      );

      setExportMessage({
        type: "success",
        text: `CSV exported successfully: ${result.filename}`,
      });

      // Call success callback if export was successful
      if (onExportSuccess) {
        onExportSuccess(tableHeader);
      }
    } catch (error) {
      setExportMessage({
        type: "error",
        text: "Export failed: " + error.message,
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [tableHeader, exportColumnIds, filename, clearMessage, onExportSuccess]);

  // Export as Excel via API
  const handleExportExcel = useCallback(async () => {
    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return;
    }

    setIsExporting(true);
    setExportingFormat('excel');
    setExportMessage({ type: "loading", text: "Preparing Excel export..." });

    try {
      const result = await cloudApi.downloadExport(
        tableHeader.id,
        'excel',
        exportColumnIds,
        filename.replace(/\.(csv|json)$/, '.xlsx')
      );

      setExportMessage({
        type: "success",
        text: `Excel exported successfully: ${result.filename}`,
      });

      // Call success callback if export was successful
      if (onExportSuccess) {
        onExportSuccess(tableHeader);
      }
    } catch (error) {
      setExportMessage({
        type: "error",
        text: "Export failed: " + error.message,
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [tableHeader, exportColumnIds, filename, clearMessage, onExportSuccess]);

  // Export as JSON via API
  const handleExportJSON = useCallback(async () => {
    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return;
    }

    setIsExporting(true);
    setExportingFormat('json');
    setExportMessage({ type: "loading", text: "Preparing JSON export..." });

    try {
      const result = await cloudApi.downloadExport(
        tableHeader.id,
        'json',
        exportColumnIds,
        filename.replace(/\.(csv|xlsx)$/, '.json')
      );

      setExportMessage({
        type: "success",
        text: `JSON exported successfully: ${result.filename}`,
      });

      // Call success callback if export was successful
      if (onExportSuccess) {
        onExportSuccess(tableHeader);
      }
    } catch (error) {
      setExportMessage({
        type: "error",
        text: "Export failed: " + error.message,
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [tableHeader, exportColumnIds, filename, clearMessage, onExportSuccess]);

  // Export as Shopify product-import CSV via API (Handle-grouped collapse). Backend gates this to
  // Shopify-extraction tables (metadata.specificExtractionType === "shopify") and ignores column
  // subsets (fixed Shopify header set), so no columnIds are sent.
  const handleExportShopify = useCallback(async () => {
    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return;
    }

    setIsExporting(true);
    setExportingFormat('shopify');
    setExportMessage({ type: "loading", text: "Preparing Shopify CSV export..." });

    try {
      const result = await cloudApi.downloadExport(
        tableHeader.id,
        'shopify',
        null,
        filename.replace(/\.(csv|xlsx|json)$/i, '') + '-shopify.csv'
      );

      setExportMessage({
        type: "success",
        text: `Shopify CSV exported successfully: ${result.filename}`,
      });

      if (onExportSuccess) {
        onExportSuccess(tableHeader);
      }
    } catch (error) {
      setExportMessage({
        type: "error",
        text: "Export failed: " + error.message,
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [tableHeader, filename, clearMessage, onExportSuccess]);

  // Copy to clipboard (fetches all rows from API, limited to CLIPBOARD_ROW_LIMIT)
  const handleCopyToClipboard = useCallback(async () => {
    // Check if clipboard is disabled due to row limit
    if (clipboardDisabled) {
      setExportMessage({
        type: "error",
        text: `Copy to clipboard is limited to ${CLIPBOARD_ROW_LIMIT.toLocaleString()} rows. Please use CSV export instead.`,
      });
      clearMessage();
      return { success: false, message: clipboardDisabledReason };
    }

    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return { success: false, message: "No table selected" };
    }

    setIsExporting(true);
    setExportingFormat('clipboard');
    setExportMessage({ type: "loading", text: "Fetching data..." });

    try {
      // Fetch ALL rows from API (up to the limit)
      const tableData = await cloudApi.getTableWithRows(tableHeader.id, {
        limit: CLIPBOARD_ROW_LIMIT,
      });

      if (!tableData?.rows || tableData.rows.length === 0) {
        setExportMessage({ type: "error", text: "No data to copy" });
        clearMessage();
        return { success: false, message: "No data to copy" };
      }

      const result = await copyToClipboard(tableData.rows, exportableColumns);

      setExportMessage({
        type: result.success ? "success" : "error",
        text: result.message,
      });

      // Call success callback if copy was successful
      if (result.success && onExportSuccess) {
        onExportSuccess(tableHeader);
      }

      return result;
    } catch (error) {
      const errorResult = {
        success: false,
        message: "Copy failed: " + error.message,
      };

      setExportMessage({
        type: "error",
        text: errorResult.message,
      });

      return errorResult;
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [clipboardDisabled, clipboardDisabledReason, tableHeader, exportableColumns, clearMessage, onExportSuccess]);

  // Google Sheets integration status (lazy-loaded when export menu opens)
  const [googleSheetsStatus, setGoogleSheetsStatus] = useState(null); // null = not fetched, { connected, email }

  const fetchGoogleSheetsStatus = useCallback(async () => {
    try {
      const integrations = await cloudApi.getIntegrations();
      const googleSheets = integrations.find((i) => i.provider === "google_sheets");
      setGoogleSheetsStatus({
        connected: googleSheets?.status === "active",
        email: googleSheets?.account_email || null,
      });
    } catch {
      setGoogleSheetsStatus({ connected: false, email: null });
    }
  }, []);

  // Export to Google Sheets via backend API
  const handleExportToGoogleSheets = useCallback(async () => {
    if (!tableHeader?.id) {
      setExportMessage({ type: "error", text: "No table selected" });
      clearMessage();
      return;
    }

    setIsExporting(true);
    setExportingFormat('googlesheets');
    setExportMessage({ type: "loading", text: "Exporting to Google Sheets..." });

    try {
      const result = await cloudApi.exportToGoogleSheets(tableHeader.id);

      // Open the spreadsheet in a new tab
      if (result.spreadsheetUrl) {
        window.open(result.spreadsheetUrl, "_blank");
      }

      setExportMessage({
        type: "success",
        text: `Exported ${result.rowCount?.toLocaleString() || ""} rows to Google Sheets`,
      });

      if (onExportSuccess) {
        onExportSuccess(tableHeader);
      }
    } catch (error) {
      setExportMessage({
        type: "error",
        text: error.message || "Export to Google Sheets failed",
      });
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
      clearMessage();
    }
  }, [tableHeader, clearMessage, onExportSuccess]);

  // Export with format selection
  const handleExport = useCallback(
    async (format = "csv") => {
      switch (format.toLowerCase()) {
        case "csv":
          return handleExportCSV();
        case "excel":
        case "xlsx":
          return handleExportExcel();
        case "json":
          return handleExportJSON();
        case "shopify":
          return handleExportShopify();
        case "clipboard":
          return handleCopyToClipboard();
        case "googlesheets":
        case "google-sheets":
          return handleExportToGoogleSheets();
        default:
          setExportMessage({
            type: "error",
            text: `Unsupported export format: ${format}`,
          });
          clearMessage();
      }
    },
    [handleExportCSV, handleExportExcel, handleExportJSON, handleExportShopify, handleCopyToClipboard, handleExportToGoogleSheets, clearMessage]
  );

  // Check if export is available
  const canExport = useMemo(() => {
    return tableHeader && exportableColumns && exportableColumns.length > 0;
  }, [tableHeader, exportableColumns]);

  return {
    // Export functions
    handleExportCSV,
    handleExportExcel,
    handleExportJSON,
    handleExportShopify,
    handleCopyToClipboard,
    handleExportToGoogleSheets,
    handleExport,

    // State
    isExporting,
    exportingFormat,
    exportMessage,
    canExport,

    // Google Sheets integration status
    googleSheetsStatus,
    fetchGoogleSheetsStatus,

    // Clipboard state
    clipboardDisabled,
    clipboardDisabledReason,

    // Statistics
    exportStats,

    // Utility functions
    clearExportMessage: () => setExportMessage(null),
  };
};
